import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';
import api from '../api';

const getAuthErrorMessage = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const backendMsg =
    data?.message ||
    data?.error ||
    (Array.isArray(data?.errors) ? data.errors.join(', ') : '') ||
    '';

  // Normalize common auth failures
  if (status === 400 || status === 401 || status === 403) {
    // If backend provides a specific reason, show it; otherwise generic
    if (/invalid|bad credentials|wrong|username|password|email/i.test(backendMsg)) {
      return 'Invalid email/username or password.';
    }
    return 'Invalid email/username or password.';
  }

  // Other cases: show backend message if available, else generic
  return backendMsg || err?.message || 'Login failed. Please try again.';
};

const Login = () => {
    const [formData, setFormData] = useState({ username: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const { login, hasRole, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Check if user is already authenticated
    useEffect(() => {
        if (isAuthenticated()) {
            navigate('/dashboard', { replace: true });
        }
    }, [isAuthenticated, navigate]);

    const handleChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        if (!formData.username || !formData.password) {
            setError('Please enter both email/username and password.');
            setIsLoading(false);
            return;
        }

        try {
            const res = await api.post('/auth/signin', formData);

            if (!res.data || !res.data.token) {
                throw new Error('Invalid response from server');
            }

            login({
                ...res.data,
                token: res.data.token,
                refreshToken: res.data.refreshToken,
            });

            // Check role and redirect accordingly
            if (hasRole('ROLE_ADMIN')) {
                navigate('/dashboard', { replace: true });
            } else if (hasRole('ROLE_FINANCE')) {
                navigate('/finance', { replace: true });
            } else if (hasRole('ROLE_HRD')) {
                navigate('/hrd', { replace: true });
            } else if (hasRole('ROLE_MARKETING')) {
                navigate('/marketing', { replace: true });
            } else if (hasRole('ROLE_IT')) {
                navigate('/it', { replace: true });
            } else {
                navigate('/dashboard', { replace: true });
            }
        } catch (err) {
            console.error('Login error:', err);
            setError(getAuthErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="d-flex flex-column bg-white">
            <div className="row g-0 flex-fill">
                <div className="col-12 col-lg-6 col-xl-4 border-top-wide border-primary d-flex flex-column justify-content-center">
                    <div className="container container-tight my-5 px-lg-5">
                        <div className="text-center mb-4">
                            <img src="./static/logo.png" width={160} alt="Logo" />
                        </div>
                        <h2 className="h3 text-center mb-3">Login to your account</h2>
                        <form onSubmit={handleSubmit} autoComplete="off">
                            <div className="mb-3">
                                <label className="form-label">Email or Username</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="username"
                                    placeholder="Enter your email or username"
                                    value={formData.username}
                                    onChange={handleChange}
                                    autoComplete="off"
                                    disabled={isLoading}
                                />
                            </div>
                            <div className="mb-2">
                                <label className="form-label">
                                    Password
                                    <span className="form-label-description">
                                        <Link to="/forgot-password">I forgot password</Link>
                                    </span>
                                </label>
                                <div className="input-group input-group-flat">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className="form-control"
                                        name="password"
                                        placeholder="Your password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        autoComplete="off"
                                        disabled={isLoading}
                                    />
                                    <span className="input-group-text">
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="border-0 bg-transparent p-0 m-0"
                                            title={showPassword ? 'Hide password' : 'Show password'}
                                            style={{ cursor: 'pointer' }}
                                            disabled={isLoading}
                                        >
                                            {showPassword ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                                    <line x1="1" y1="1" x2="23" y2="23" />
                                                </svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                    <circle cx="12" cy="12" r="3" />
                                                </svg>
                                            )}
                                        </button>
                                    </span>
                                </div>
                            </div>
                            {error && (
                                <div className="alert alert-danger py-2 mb-2">
                                    {error}
                                </div>
                            )}
                            <div className="mb-2">
                                <label className="form-check">
                                    <input type="checkbox" className="form-check-input" />
                                    <span className="form-check-label">Remember me on this device</span>
                                </label>
                            </div>
                            <div className="form-footer">
                                <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
                                    {isLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Signing in...
                                        </>
                                    ) : (
                                        'Sign in'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                <div className="col-12 col-lg-6 col-xl-8 d-none d-lg-block">
                    <div className="bg-cover h-100 min-vh-100" style={{ backgroundImage: 'url(./static/photos/login-picture.jpeg)' }}></div>
                </div>
            </div>
        </div>
    );
};

export default Login;
